import React, { createContext, useContext, useEffect, useState } from "react";
import { Avatar, List, notification } from "antd";

const CartContext = createContext();

const IMAGE_BASE_URL = "https://1fiveglobal.in/uploads/productsImages/";

// Helper functions with safe fallbacks
export const getProductName = (value) =>
  value?.name ?? value?.product_name ?? value?.Product_Name ?? value?.productName ?? "Product";

export const getColorName = (value) =>
  value?.color_name ?? value?.name ?? value?.colour ?? value?.productColor ?? "Default Color";

export const getSizeName = (value) =>
  value?.product_size ?? value?.name ?? value?.size_name ?? value?.productSize ?? "Default Size";

export const getProductImage = (value) => {
  if (!value) return "";
  let imgPath = "";
  if (Array.isArray(value)) {
    imgPath = value[0]?.image ?? value[0]?.images ?? "";
  } else if (typeof value === "object") {
    imgPath = value.productImage ?? value.image ?? value.images?.[0]?.image ?? "";
  } else if (typeof value === "string") {
    imgPath = value;
  }
  if (!imgPath) return "";
  return imgPath.startsWith("http") ? imgPath : `${IMAGE_BASE_URL}${imgPath}`;
};

export const useCartContext = () => useContext(CartContext);

export const CartProvider = ({ children }) => {
  const [cartValue, setCartValue] = useState([]);
  const [cartCount, setCartCount] = useState(0);
  const [showCartList, setShowCartList] = useState(false);
  const [api, contextHolder] = notification.useNotification();

  const [amountDetails, setAmountDetails] = useState({
    totalAmount: 0,
    subTotal: 0,
    taxAmount: 0,
  });

  // Selected product state
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedSize, setSelectedSize] = useState(null);
  const [selectedColor, setSelectedColor] = useState(null);
  const [selectedPrice, setSelectedPrice] = useState(0);
  const [selectedImages, setSelectedImages] = useState([]);

  // Calculate totals whenever cart changes
  const calculateCartTotals = (items) => {
    const subTotal = items.reduce((sum, item) => sum + (parseFloat(item.product_price) || 0), 0);
    const taxAmount = parseFloat(((subTotal * 10) / 100).toFixed(2));
    const totalAmount = parseFloat((subTotal + taxAmount).toFixed(2));

    setAmountDetails({
      subTotal: parseFloat(subTotal.toFixed(2)),
      taxAmount,
      totalAmount,
    });
  };

  const saveToLocalStorage = (data) => {
    localStorage.setItem("cart", JSON.stringify(data));
  };

  // Load cart from LocalStorage on mount
  useEffect(() => {
    const storedCart = localStorage.getItem("cart");
    if (storedCart) {
      try {
        const parsedCart = JSON.parse(storedCart);
        if (Array.isArray(parsedCart)) {
          setCartValue(parsedCart);
          setCartCount(
            parsedCart.reduce((total, currentItem) => {
              return total + (parseInt(currentItem.productQuantity, 10) || 1);
            }, 0)
          );
          calculateCartTotals(parsedCart);
        }
      } catch (e) {
        console.error("Failed to parse cart from LocalStorage:", e);
      }
    }
  }, []);

  const resolveProductData = (product, color, size, images, price) => {
    return {
      productName: getProductName(product),
      productColor: getColorName(color),
      productSize: getSizeName(size),
      productImage: getProductImage(images),
      productPrice: parseFloat(price) || 0,
    };
  };

  const openCartDrawer = () => {
    const cartDrawer = document.getElementById("offcanvasRight");

    if (cartDrawer && window.bootstrap?.Offcanvas) {
      window.bootstrap.Offcanvas.getOrCreateInstance(cartDrawer).show();
    }
  };

  // Notification popup
  const openNotification = (item, message) => {
    const title = getProductName(item);
    const color = getColorName(item);
    const size = getSizeName(item);
    const image = getProductImage(item);
    const notificationKey = `cart-notif-${Date.now()}`;

    api.open({
      message,
      description: (
        <List itemLayout="horizontal">
          <List.Item>
            <List.Item.Meta
              avatar={<Avatar src={image} shape="square" size="large" />}
              title={`${title} (${color})`}
              description={`Price: Rs. ${item.fixedPrice} | Qty: ${item.productQuantity} | Size: ${size}`}
            />
          </List.Item>
        </List>
      ),
      key: notificationKey,
      duration: 4,
      onClick: () => {
        api.destroy(notificationKey);
        openCartDrawer();
      },
      style: { cursor: "pointer" },
    });
  };

  // Add Item to Cart Handler
  const handleAddCart = (payload) => {
    const qtyToAdd = parseInt(payload.Quantity, 10) || 1;
    const details = resolveProductData(
      selectedProduct,
      selectedColor,
      selectedSize,
      selectedImages,
      selectedPrice
    );

    const targetColorId = selectedColor?.color_id ?? selectedColor?.id;
    const targetSizeId = selectedSize?.size_id ?? selectedSize?.id;

    const existingIndex = cartValue.findIndex((item) => {
      const cId = item.colorid ?? item.color_id;
      const sId = item.sizeid ?? item.size_id;
      return cId === targetColorId && sId === targetSizeId;
    });

    let updatedCart = [];

    if (existingIndex > -1) {
      updatedCart = [...cartValue];
      const existingItem = updatedCart[existingIndex];
      const newQty = parseInt(existingItem.productQuantity, 10) + qtyToAdd;
      const unitPrice = parseFloat(existingItem.fixedPrice);

      updatedCart[existingIndex] = {
        ...existingItem,
        productQuantity: newQty.toString(),
        product_price: unitPrice * newQty,
      };

      openNotification(updatedCart[existingIndex], `Updated quantity to ${newQty}`);
    } else {
      const newItem = {
        productid: selectedProduct?.product_Id ?? selectedProduct?.id,
        product_type: selectedProduct?.product_type ?? selectedProduct?.category_name,
        colorid: targetColorId,
        sizeid: targetSizeId,
        productName: details.productName,
        productSize: details.productSize,
        productColor: details.productColor,
        fixedPrice: details.productPrice,
        product_price: details.productPrice * qtyToAdd,
        productImage: details.productImage,
        productQuantity: qtyToAdd.toString(),
      };

      updatedCart = [...cartValue, newItem];
      openNotification(newItem, "Added to cart successfully");
    }

    setCartValue(updatedCart);
    setCartCount(
      updatedCart.reduce((total, currentItem) => {
        return total + (parseInt(currentItem.productQuantity, 10) || 1);
      }, 0)
    );
    saveToLocalStorage(updatedCart);
    calculateCartTotals(updatedCart);
  };

  const contextValue = {
    handleAddCart,
    cartCount,
    setCartCount,
    cartValue,
    setCartValue,
    showCartList,
    setShowCartList,
    amountDetails,
    openCartDrawer,
    selectedProduct,
    setSelectedProduct,
    selectedSize,
    setSelectedSize,
    selectedColor,
    setSelectedColor,
    selectedPrice,
    setSelectedPrice,
    selectedImages,
    setSelectedImages,
  };

  return (
    <CartContext.Provider value={contextValue}>
      {contextHolder}
      {children}
    </CartContext.Provider>
  );
};

export default CartProvider;